import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { springs } from "@/lib/motion";
import { SButton } from "@/components/common/SButton";

const ConfirmContext = createContext();

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolver = useRef(null);
  const acceptRef = useRef(null);

  const confirm = useCallback(
    (opts) =>
      new Promise((resolve) => {
        resolver.current = resolve;
        setState({ title: "Are you sure?", message: "", confirmText: "Confirm", cancelText: "Cancel", destructive: false, ...opts });
      }),
    []
  );

  const close = (val) => {
    if (resolver.current) resolver.current(val);
    resolver.current = null;
    setState(null);
  };

  // Escape cancels; move focus to the Confirm button when the modal opens.
  useEffect(() => {
    if (!state) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(false); }
    };
    document.addEventListener("keydown", onKeyDown);
    const raf = requestAnimationFrame(() => acceptRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      cancelAnimationFrame(raf);
    };
  }, [state]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AnimatePresence>
        {state && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => close(false)}
            data-testid="confirm-overlay"
            style={{ position: "fixed", inset: 0, zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(15,15,40,0.45)", backdropFilter: "blur(4px)" }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={springs.card}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-title"
              data-testid="confirm-modal"
              className="glass-card"
              style={{ maxWidth: 400, width: "100%", padding: 28, background: "var(--bg-surface)" }}
            >
              <h3 id="confirm-title" className="text-h3" style={{ marginBottom: 8 }}>{state.title}</h3>
              {state.message && <p style={{ color: "var(--text-secondary)", marginBottom: 24, fontSize: 15 }}>{state.message}</p>}
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: state.message ? 0 : 24 }}>
                <SButton variant="secondary" onClick={() => close(false)} data-testid="confirm-cancel">{state.cancelText}</SButton>
                <SButton
                  ref={acceptRef}
                  variant="primary"
                  onClick={() => close(true)}
                  data-testid="confirm-accept"
                  style={state.destructive ? { background: "var(--error)" } : undefined}
                >
                  {state.confirmText}
                </SButton>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
